import { useCallback } from 'react';

import { Project as ProjectModel } from '@material-bank/projects-api';
import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import {
  useCurrentProject,
  useProjectsProvider,
  useValidateProjectData,
} from '@material-bank/projects-utils';
import {
  shouldTriggerProjectSelectionDialog,
  AnalyticsEventLocation,
  CustomEventName,
  Project,
} from '@mb/lib';

import { isUnavailable } from '@productSearch/utils/helpers/isUnavaialble';
import { openRestrictedItemModal } from '@restrictedItemsModal';
import { useDispatch } from 'react-redux';
import {
  goToLogin,
  triggerAssociatedProductsModal,
} from '../../../pages/ProductSearch/utils/helpers';
import { sendAnalytics } from '../api/events';
import { getEventsMaterialProps } from '../helpers';
import useAddToBag from '../hooks/useAddToBag';
import {
  setIsProjectModalOpen,
  setProjectModalEventLocation,
} from '../redux/sliceCreators/projectsSlice';
import { setMaterialsInQueue } from '../redux/sliceCreators/searchSlice';
import { ProductSearchMaterial, RelatedProduct } from '../types';
import { getCookieByName } from '../utils';

export interface AddToCartProps {
  product: ProductSearchMaterial;
  isMobile: boolean;
  productEventData: {
    subtitle1: string;
    subtitle2: string;
  };
  analyticsEventLocation: AnalyticsEventLocation;
  relatedProductData?: RelatedProduct;
  shouldTriggerRecModal?: boolean;
  additionalSplitProperties?: Record<string, string>;
  listId?: string;
  listName?: string;
}

const useAddToBagWithValidations = () => {
  const { isLoading, loadingIds, addToBag } = useAddToBag();
  const { activeProjects } = useProjectsProvider();
  const { setCurrentProjectId } = useCurrentProject();
  const { isProjectDataComplete } = useValidateProjectData();

  const dispatch = useDispatch();

  const onCreateProjectClick = useCallback(
    (params?: { analyticsEventLocation?: string }) => {
      const { analyticsEventLocation } = params ?? {};

      dispatch(setIsProjectModalOpen(true));

      if (analyticsEventLocation) {
        dispatch(setProjectModalEventLocation(analyticsEventLocation));
      }
    },
    [],
  );

  const handleAddToBag = (
    props: AddToCartProps,
    currentProject: ProjectModel | null,
  ) => {
    const loogedCookie = getCookieByName('loggedIn');
    if (loogedCookie === undefined || loogedCookie === null) {
      goToLogin();
      return;
    }

    const {
      product,
      isMobile = false,
      productEventData,
      analyticsEventLocation,
      relatedProductData,
      shouldTriggerRecModal,
      additionalSplitProperties,
      listId,
      listName,
    } = props;

    const selectedProject = {
      id: currentProject?.externalId || 0,
      label: currentProject?.title ?? 'Select project..',
    };

    const analyticsProps = getEventsMaterialProps(product);
    const isFinishMaterial = product?.isFinish;
    const originRequestId = product?.originRequestId;

    // Check variant availability

    // Restriction SIM validations
    if (
      (product.isGeoRestricted ||
        product.isProjectRestricted ||
        product.isUnavailable ||
        isUnavailable(product, product.entityId)) &&
      !isMobile
    ) {
      sendAnalytics(
        {
          id: [String(product.entityId)],
          type: 'view',
          ...analyticsProps,
        },
        1200,
      );

      openRestrictedItemModal({
        product,
        subtitle1: productEventData?.subtitle1 || '',
        subtitle2: productEventData?.subtitle2 || '',
        projectId: selectedProject?.id || '',
      });

      return;
    }

    // Validate if project selection dialog should be triggered
    if (
      (product?.associatedFinishProducts?.length ?? 0) > 0 &&
      isFinishMaterial
    ) {
      // If Finishes - trigger modal to select associated Products
      const productsIds = product?.associatedFinishProducts?.join('|');
      const productsModalData = {
        id: product?.entityId,
        name: product?.name || product.title,
        manufacturerName: product?.manufacturer,
        productImageUrl: product?.imageUrl || product.thumbnailImageUrl,
      };

      if (shouldTriggerProjectSelectionDialog()) {
        window.dispatchEvent(
          new CustomEvent(CustomEventName.OPEN_PROJECT_SELECTION_DIALOG, {
            detail: {
              currentProjectId: selectedProject?.id,
              analyticsEventLocation,
              onCurrentProjectSave: (project: any) => {
                const projectId = project?.externalId || project?.project_id;

                if (projectId) {
                  setCurrentProjectId(
                    project?.project_id || project?.externalId,
                  );
                }

                triggerAssociatedProductsModal(productsIds, productsModalData);
              },
            },
          }),
        );
        return;
      }

      triggerAssociatedProductsModal(productsIds, productsModalData);

      return;
    }

    const currentProduct = product;
    if (originRequestId != null) {
      currentProduct['originRequestId'] = originRequestId;
    }

    if (
      Object.keys(activeProjects)?.length > 0 &&
      Boolean(selectedProject.id)
    ) {
      const gaEventLocation = analyticsEventLocation || 'Catalog';

      if (shouldTriggerProjectSelectionDialog()) {
        window.dispatchEvent(
          new CustomEvent(CustomEventName.OPEN_PROJECT_SELECTION_DIALOG, {
            detail: {
              currentProjectId: selectedProject?.id,
              analyticsEventLocation,
              onCurrentProjectSave: (project: Project | ProjectModel) => {
                // No validation needed here - the project has been validated in the selection dialog
                addToBag({
                  materials: [currentProduct],
                  // @ts-expect-error the project model needs to be unified
                  projectId: project?.externalId || project?.project_id,
                  project,
                  relatedProductData,
                  triggerLocation: gaEventLocation,
                  isFinishMaterial: false,
                  shouldTriggerModal: shouldTriggerRecModal,
                  additionalSplitProperties,
                  additionalAnalyticsProperties: {
                    listId,
                    listName,
                  },
                });
              },
            },
          }),
        );

        return;
      }

      const project = activeProjects[selectedProject.id];

      // If the project is not found, open the create project modal
      if (!project) {
        window.dispatchEvent(
          new CustomEvent('open-create-project-modal', {
            detail: {
              eventLocation: 'Search',
            },
          }),
        );

        dispatch(setMaterialsInQueue([currentProduct]));

        return;
      }

      // Complete any missing project data
      if (!isProjectDataComplete(project)) {
        projectsEditDialogConfig.openProjectEditDialog({
          projectId: selectedProject.id,
          successCallback: async () => {
            addToBag({
              materials: [currentProduct],
              projectId: selectedProject.id,
              relatedProductData,
              triggerLocation: gaEventLocation,
              isFinishMaterial: false,
              shouldTriggerModal: shouldTriggerRecModal,
              additionalSplitProperties,
              additionalAnalyticsProperties: {
                listId,
                listName,
              },
            });
          },
        });

        return;
      }

      addToBag({
        materials: [currentProduct],
        projectId: selectedProject.id,
        relatedProductData,
        triggerLocation: gaEventLocation,
        isFinishMaterial: false,
        shouldTriggerModal: shouldTriggerRecModal,
        additionalSplitProperties,
        additionalAnalyticsProperties: {
          listId,
          listName,
        },
      });
    } else {
      onCreateProjectClick?.();
      dispatch(setMaterialsInQueue([currentProduct]));
    }
  };

  return { isLoading, loadingIds, handleAddToBag };
};

export default useAddToBagWithValidations;
